// MSSQL renderer-side INDEX introspection. Sibling to
// `mssqlSchemaIntrospection.ts` (FK edges / routines / sequences) and
// `mssqlColumnIntrospection.ts` (columns + CHECK clauses); split out for the
// same reason they are — each file stays inside the 200-line budget and owns
// one artefact.
//
// The index NAME is a completion vocabulary of its own: `DROP INDEX ▮ ON t`
// takes an index, not a table or a column. The bulk query here is joined into
// the existing `MssqlTableEntry` map by `mergeTableIntrospection`.
//
// S7 item 2 — paged by `(object_id, index_id, key_ordinal)` off the
// `sys.index_columns` clustered key. One index's key columns arrive as N rows,
// so the paged loop carries a straddling `(object_id, index_id)` group over to
// the next page (`groupKeyOf`) — otherwise an index split across a page
// boundary would aggregate into two half-indexes.

import type { IndexMeta } from '@/core/engines/intellisenseTypes';
import type { IntrospectionRow, PagedIntrospectionOutcome } from './introspectionPaging';
import { fetchMssqlPagedArtefact, type PagedFetchOptions } from './mssqlIntrospectionQuery';

// TOTAL row budget across all pages (S7 item 4) — one row per index key
// column. Not a per-request cap: the per-page guard is
// `INTROSPECTION_PAGE_MAX_ROWS`. See `introspectionBudgets.ts`.
import { INDEX_ROW_BUDGET } from './introspectionBudgets';

/** Sink handed one page's worth of aggregated indexes (S7 item 1's phase 2). */
export type MssqlIndexPageSink = (
  indexes: readonly MssqlIndexIntrospection[],
) => void | Promise<void>;

export function buildMssqlIndexesPageSql(offsetFetch: string): string {
  return `SELECT
  s.name AS TableSchema,
  t.name AS TableName,
  i.name AS IndexName,
  i.is_unique AS IsUnique,
  i.is_primary_key AS IsPrimary,
  c.name AS ColumnName,
  ic.key_ordinal AS Ord,
  i.object_id AS ObjectId,
  i.index_id AS IndexId
FROM sys.indexes i
JOIN sys.tables t ON t.object_id = i.object_id
JOIN sys.schemas s ON s.schema_id = t.schema_id
JOIN sys.index_columns ic
  ON ic.object_id = i.object_id AND ic.index_id = i.index_id
JOIN sys.columns c
  ON c.object_id = ic.object_id AND c.column_id = ic.column_id
WHERE i.name IS NOT NULL AND ic.is_included_column = 0
ORDER BY i.object_id, i.index_id, ic.key_ordinal
${offsetFetch}`;
}

/**
 * Page index metadata for every user table in the database — the INDEX-NAME
 * vocabulary of `DROP INDEX ▮ ON t` — into `sink`.
 *
 * `ic.is_included_column = 0` keeps an `INCLUDE (…)` payload out of
 * `columns`: an included column is not part of the index key. Heaps and the
 * unnamed clustered-PK-less rows are excluded by `i.name IS NOT NULL`.
 * Failures degrade to whatever pages already landed (the caller's `.catch`
 * keeps the snapshot), mirroring `fetchMssqlRoutines`.
 */
export async function fetchMssqlIndexPages(
  connectionId: string,
  database: string,
  sink: MssqlIndexPageSink,
  options?: PagedFetchOptions,
): Promise<PagedIntrospectionOutcome> {
  return fetchMssqlPagedArtefact<MssqlIndexIntrospection>({
    connectionId,
    database,
    label: 'indexes',
    artefact: 'indexes',
    sqlForPage: buildMssqlIndexesPageSql,
    aggregate: aggregateIndexRows,
    sink,
    totalBudget: INDEX_ROW_BUDGET,
    groupKeyOf: (row) => `${String(row.ObjectId ?? '')}|${String(row.IndexId ?? '')}`,
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating convenience wrapper over `fetchMssqlIndexPages`. */
export async function fetchMssqlIndexes(
  connectionId: string,
  database: string,
): Promise<MssqlIndexIntrospection[]> {
  const out: MssqlIndexIntrospection[] = [];
  await fetchMssqlIndexPages(connectionId, database, (page) => {
    out.push(...page);
  });
  return out;
}

/** One index, owned by `(schema, table)`. The bus-facing `IndexMeta` shape
 *  plus the ownership routing `mergeTableIntrospection` keys on. */
export type MssqlIndexIntrospection = {
  readonly schema: string;
  readonly table: string;
  readonly index: IndexMeta;
};

type IndexRow = {
  readonly TableSchema: unknown;
  readonly TableName: unknown;
  readonly IndexName: unknown;
  readonly IsUnique: unknown;
  readonly IsPrimary: unknown;
  readonly ColumnName: unknown;
  readonly Ord: unknown;
};

/**
 * Aggregate the per-key-column rows into one `IndexMeta` per
 * `(schema, table, index)`. Exported so the unit tests exercise the grouping
 * without standing up the IPC bridge (mirrors `aggregateFkRows`).
 */
export function aggregateIndexRows(
  rows: readonly IntrospectionRow[],
): MssqlIndexIntrospection[] {
  type Pending = {
    schema: string;
    table: string;
    name: string;
    isUnique: boolean;
    isPrimary: boolean;
    cols: { ord: number; col: string }[];
  };
  const byIndex = new Map<string, Pending>();
  for (const raw of rows) {
    const row = raw as IndexRow;
    const schema = String(row.TableSchema ?? '').trim();
    const table = String(row.TableName ?? '').trim();
    const name = String(row.IndexName ?? '').trim();
    const column = String(row.ColumnName ?? '').trim();
    if (!schema || !table || !name) continue;
    const key = `${schema.toLowerCase()}|${table.toLowerCase()}|${name.toLowerCase()}`;
    let entry = byIndex.get(key);
    if (!entry) {
      entry = {
        schema,
        table,
        name,
        isUnique: isIndexFlagTrue(row.IsUnique),
        isPrimary: isIndexFlagTrue(row.IsPrimary),
        cols: [],
      };
      byIndex.set(key, entry);
    }
    if (column) entry.cols.push({ ord: Number(row.Ord ?? 0), col: column });
  }
  const out: MssqlIndexIntrospection[] = [];
  for (const entry of byIndex.values()) {
    entry.cols.sort((a, b) => a.ord - b.ord);
    out.push({
      schema: entry.schema,
      table: entry.table,
      index: {
        name: entry.name,
        columns: entry.cols.map((c) => c.col),
        isUnique: entry.isUnique,
        isPrimary: entry.isPrimary,
      },
    });
  }
  return out;
}

/** `sys.indexes` bit columns arrive as booleans from tedious and as `0`/`1`
 *  from drivers that widen `bit` to a number. */
function isIndexFlagTrue(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value.trim() === '1' || value.trim().toLowerCase() === 'true';
  return false;
}

